import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';

type Listener = () => void;

export type SelectableContextType<ValueType> = {
  subscribe: (listener: Listener) => () => void;
  replaceState: (value: ValueType) => void;
  getState: () => ValueType;
};

export const createSelectableContext = <ValueType>(defaultValue: ValueType) => {
  const Context = createContext<SelectableContextType<ValueType>>({
    subscribe: () => {
      // noop
      return () => {
        // noop cleanup
      };
    },
    replaceState: () => {
      // noop
    },
    getState: () => defaultValue,
  });

  return {
    Context,
    useCreateContextValueStore: (initialState: ValueType) =>
      useMemo(() => {
        let state = initialState;
        const getState = () => state;
        const listeners = new Set<Listener>();
        const replaceState = (newState: ValueType) => {
          state = newState;
          listeners.forEach((l) => l());
        };
        const subscribe = (listener: Listener) => {
          listeners.add(listener);
          return () => listeners.delete(listener);
        };
        return {getState, replaceState, subscribe};
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []),

    useContextValueSelector: <SelectedValue>(
      selector: (value: ValueType) => SelectedValue,
    ) => {
      const {subscribe, getState} = useContext(Context);
      const getSnapshot = useCallback(
        () => selector(getState()),
        [selector, getState],
      );
      return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    },
  };
};
